import { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { Room, Table, WaitlistEntry, TableStatus } from '@shared/types';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorBoundary } from '../components/common/ErrorBoundary';
import { ScrollingMessage } from '../components/common/ScrollingMessage';

// Logo size configuration
const LOGO_SIZES = {
  small: 60,
  medium: 80,
  large: 120,
  xl: 160
} as const;

export function TVDashboard() {
  const { userProfile } = useAuth();
  const [searchParams] = useSearchParams();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [tables, setTables] = useState<Table[]>([]);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [tablesLoading, setTablesLoading] = useState(true);
  const [roomsError, setRoomsError] = useState<Error | null>(null);
  const [tablesError, setTablesError] = useState<Error | null>(null);
  
  // Get the current room for this admin
  // Priority: 1) Query parameter roomId, 2) userProfile roomId
  const currentRoomId = searchParams.get('roomId') || userProfile?.roomId;

  // Direct Firebase approach for rooms
  useEffect(() => {
    if (!currentRoomId || !db) {
      setRoomsLoading(false);
      return;
    }

    const roomsRef = collection(db, 'rooms');
    
    const unsubscribe = onSnapshot(
      roomsRef,
      (snapshot) => {
        const roomsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as Room[];
        setRooms(roomsData);
        setRoomsLoading(false);
      },
      (err) => {
        setRoomsError(err as Error);
        setRoomsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentRoomId]);

  // Direct Firebase approach for tables
  useEffect(() => {
    if (!currentRoomId || !db) {
      setTables([]);
      setTablesLoading(false);
      return;
    }

    const tablesRef = collection(db, 'rooms', currentRoomId, 'tables');
    
    const unsubscribe = onSnapshot(
      tablesRef,
      (snapshot) => {
        const tablesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as Table[];
        setTables(tablesData);
        setTablesLoading(false);
      },
      (err) => {
        setTablesError(err as Error);
        setTablesLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentRoomId]);

  // Get waitlists for all tables - we'll fetch them individually in the component
  const roomTables = tables || [];

  if (roomsLoading || tablesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (roomsError || tablesError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-2">Error</h1>
          <p className="text-gray-300">
            {roomsError?.message || tablesError?.message || 'Failed to load data'}
          </p>
        </div>
      </div>
    );
  }

  if (!currentRoomId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-2">No Room Assigned</h1>
          <p className="text-gray-300">
            You haven't been assigned to a poker room yet.
          </p>
        </div>
      </div>
    );
  }

  const currentRoom = (rooms as Room[])?.find((room: Room) => room.id === currentRoomId);

  // Get the theme from current room (default to 'dark' for backwards compatibility)
  const tvTheme = currentRoom?.tvTheme || 'dark';

  // Filter out closed tables and sort by display_order
  const activeTables = roomTables
    .filter((table: Table) => table.status !== 'Closed')
    .sort((a: Table, b: Table) => (a.display_order || 999) - (b.display_order || 999));

  return (
    <ErrorBoundary>
      <style>
        {`
          body {
            overflow: hidden;
            margin: 0;
            padding: 0;
          }
          ::-webkit-scrollbar {
            display: none;
          }
          html, body, #root {
            height: 100%;
            overflow: hidden;
          }
        `}
      </style>
      <div className={`min-h-screen overflow-hidden ${tvTheme === 'light' ? 'bg-white text-gray-900' : 'bg-black text-white'}`} style={{ height: '100vh' }}>
        {/* Header */}
        <div className={`px-8 py-6 border-b-4 ${
          tvTheme === 'light' 
            ? 'bg-gray-50 border-blue-500' 
            : 'bg-gradient-to-r from-blue-900 to-blue-800 border-blue-600'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className={`text-4xl font-bold tracking-wider ${
                tvTheme === 'light' ? 'text-blue-600' : 'text-blue-200'
              }`}>POKER</div>
              <div className={`text-3xl font-bold tracking-wider ${
                tvTheme === 'light' ? 'text-blue-700' : 'text-white'
              }`}>WAITING LIST</div>
            </div>
            
            {/* Logo Display */}
            <div className="flex-1 flex justify-center">
              {currentRoom?.logoUrl ? (
                <img
                  src={currentRoom.logoUrl}
                  alt={`${currentRoom.name} logo`}
                  className="object-contain"
                  style={{ 
                    height: `${LOGO_SIZES[currentRoom.logoSize || 'medium']}px` 
                  }}
                />
              ) : null}
            </div>
            
            <div className="text-right">
              <div className={`text-2xl font-bold ${
                tvTheme === 'light' ? 'text-gray-900' : 'text-blue-200'
              }`}>
                {currentRoom?.name || 'Poker Room'}
              </div>
              <div className={`text-sm mt-2 ${
                tvTheme === 'light' ? 'text-gray-600' : 'text-blue-300'
              }`}>
                {new Date().toLocaleDateString('en-US', { 
                  month: '2-digit', 
                  day: '2-digit', 
                  year: '2-digit' 
                })}
              </div>
              <div className="flex items-center justify-end mt-1">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse mr-2"></div>
                <div className={`text-xs ${
                  tvTheme === 'light' ? 'text-gray-700' : 'text-green-300'
                }`}>LIVE</div>
              </div>
              <div className={`text-sm mt-1 text-right ${
                tvTheme === 'light' ? 'text-gray-600' : 'text-blue-300'
              }`}>
                WaitlistPoker.com
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">
          {activeTables.length === 0 ? (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <h2 className={`text-3xl font-bold mb-4 ${
                  tvTheme === 'light' ? 'text-gray-700' : 'text-gray-300'
                }`}>No Active Tables</h2>
                <p className={`text-lg ${
                  tvTheme === 'light' ? 'text-gray-500' : 'text-gray-400'
                }`}>All tables are currently closed</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-8">
              {activeTables.map((table: Table) => (
                <TableColumnWithWaitlist
                  key={table.id}
                  table={table}
                  roomId={currentRoomId!}
                  tvTheme={tvTheme}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer / Scrolling Message */}
        {currentRoom?.messageText ? (
          <ScrollingMessage 
            text={currentRoom.messageText}
            size={currentRoom.messageSize || 'medium'}
            speed={currentRoom.messageSpeed || 'medium'}
          />
        ) : (
          <div className="bg-gradient-to-r from-yellow-500 to-yellow-400 px-8 py-4 border-t-4 border-yellow-300">
            <div className="text-center">
              <div className="text-xl font-bold text-black tracking-wide">
                WaitlistPoker.com • Real-time Updates
              </div>
              <div className="text-sm text-yellow-800 mt-1">
                Last updated: {new Date().toLocaleTimeString()}
              </div>
            </div>
          </div>
        )}
        
        {/* Small footer text when custom message is displayed */}
        {currentRoom?.messageText && (
          <div className={`px-4 py-2 ${
            tvTheme === 'light' ? 'bg-gray-50' : 'bg-black'
          }`}>
            <div className="text-center">
              <div className={`text-xs opacity-70 ${
                tvTheme === 'light' ? 'text-gray-700' : 'text-white'
              }`}>
                WaitlistPoker.com • Real-time Updates • Last updated: {new Date().toLocaleTimeString()}
              </div>
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}

interface TableColumnWithWaitlistProps {
  table: Table;
  roomId: string;
  tvTheme: 'light' | 'dark';
}

function TableColumnWithWaitlist({ table, roomId, tvTheme }: TableColumnWithWaitlistProps) {
  const [waitlist, setWaitlist] = useState<WaitlistEntry[]>([]);
  const [waitlistLoading, setWaitlistLoading] = useState(true);
  const [waitlistError, setWaitlistError] = useState<Error | null>(null);

  useEffect(() => {
    if (!roomId || !table.id || !db) {
      setWaitlist([]);
      setWaitlistLoading(false);
      return;
    }

    const waitlistRef = collection(db, 'rooms', roomId, 'tables', table.id, 'waitlist');
    const waitlistQuery = query(waitlistRef, orderBy('position', 'asc'));
    
    const unsubscribe = onSnapshot(
      waitlistQuery,
      (snapshot) => {
        const waitlistData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as WaitlistEntry[];
        // TV Dashboard should show both waiting and notified entries
        const displayEntries = waitlistData.filter(entry => entry.status === 'waiting' || entry.status === 'notified');
        setWaitlist(displayEntries);
        setWaitlistLoading(false);
      },
      (err) => {
        setWaitlistError(err as Error);
        setWaitlistLoading(false);
      }
    );

    return () => unsubscribe();
  }, [roomId, table.id]);

  const displayPlayers = useMemo(() => {
    if (!waitlist) return [];
    
    // Filter and sort players to match waitlist order
    const filteredPlayers = (waitlist as WaitlistEntry[]).filter((entry: WaitlistEntry) => 
      entry.status === 'waiting' || entry.status === 'notified'
    );
    
    // Sort by position (which preserves chronological order)
    const sortByPosition = (a: WaitlistEntry, b: WaitlistEntry) => {
      return (a.position || 0) - (b.position || 0);
    };
    
    return filteredPlayers.sort(sortByPosition);
  }, [waitlist]);

  if (waitlistLoading) {
    return (
      <div className={`rounded-lg border-2 overflow-hidden min-h-[400px] flex items-center justify-center ${
        tvTheme === 'light' ? 'bg-white border-gray-300' : 'bg-gray-900 border-gray-700'
      }`}>
        <LoadingSpinner size="sm" />
      </div>
    );
  }

  if (waitlistError) {
    return (
      <div className={`rounded-lg border-2 overflow-hidden min-h-[400px] flex items-center justify-center ${
        tvTheme === 'light' ? 'bg-white border-gray-300' : 'bg-gray-900 border-gray-700'
      }`}>
        <div className="text-center text-red-400">
          <div className="text-sm">Error loading waitlist</div>
        </div>
      </div>
    );
  }

  return <TableColumn table={table} waitlist={displayPlayers} tvTheme={tvTheme} />;
}

interface TableColumnProps {
  table: Table;
  waitlist: WaitlistEntry[];
  tvTheme: 'light' | 'dark';
}

function TableColumn({ table, waitlist, tvTheme }: TableColumnProps) {
  const getStatusColor = (status: TableStatus) => {
    switch (status) {
      case 'Running':
        return 'bg-green-600';
      case 'Open':
        return 'bg-blue-600';
      case 'Interest':
        return 'bg-yellow-600';
      default:
        return 'bg-gray-600';
    }
  };

  const getStatusText = (status: TableStatus) => {
    switch (status) {
      case 'Running':
        return 'RUNNING';
      case 'Open':
        return 'OPEN';
      case 'Interest':
        return 'INTEREST';
      default:
        return status.toUpperCase();
    }
  };

  // Format stakes to keep original format and remove game type (e.g., "$1/$3", "$8/$16")
  const formatStakes = (stakes: string) => {
    return stakes;
  };

  return (
    <div className={`rounded-lg border-2 overflow-hidden min-h-[400px] ${
      tvTheme === 'light' ? 'bg-white border-gray-300' : 'bg-gray-900 border-gray-700'
    }`}>
      {/* Table Header */}
      <div className={`${getStatusColor(table.status)} px-6 py-4 text-center rounded-t-lg`}>
        <div className="text-xl font-bold text-white tracking-wide">
          {table.name}
        </div>
        <div className="text-lg text-gray-200 mt-1">
          {formatStakes(table.stakes)}
        </div>
        <div className="text-sm text-gray-200 mt-1">
          {getStatusText(table.status)}
        </div>
      </div>


      {/* Waitlist */}
      <div className="px-6 py-4 flex-1">
        {waitlist.length === 0 ? (
          <div className="text-center py-8">
            <div className={`text-sm ${
              tvTheme === 'light' ? 'text-gray-500' : 'text-gray-400'
            }`}>No waitlist</div>
          </div>
        ) : (
          <div className="space-y-3">
            {waitlist.slice(0, 12).map((entry: WaitlistEntry) => (
              <div 
                key={entry.id} 
                className={`flex items-center space-x-3 p-2 rounded-lg ${
                  entry.status === 'notified' 
                    ? 'bg-yellow-500 bg-opacity-20 border border-yellow-400' 
                    : ''
                }`}
              >
                <div className={`w-4 h-4 rounded-full flex-shrink-0 ${
                  entry.status === 'notified' 
                    ? 'bg-yellow-400' 
                    : 'bg-blue-500'
                }`}></div>
                <div className={`text-xl font-semibold truncate ${
                  entry.status === 'notified' 
                    ? (tvTheme === 'light' ? 'text-gray-800 font-bold' : 'text-yellow-200 font-bold')
                    : (tvTheme === 'light' ? 'text-gray-800' : 'text-white')
                }`}>
                  {/* Display current alias or name from user profile */}
                  {entry.name.toUpperCase()}
                </div>
                {/* Add globe icon for web signups */}
                {entry.added_by === 'self' && (
                  <div className="flex-shrink-0" title="Signed up via web">
                    <span className="text-xl">🌐</span>
                  </div>
                )}
              </div>
            ))}
            {waitlist.length > 12 && (
              <div className={`text-center text-xs mt-2 ${
                tvTheme === 'light' ? 'text-gray-500' : 'text-gray-400'
              }`}>
                +{waitlist.length - 12} more
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className={`px-6 py-3 border-t rounded-b-lg ${
        tvTheme === 'light' 
          ? 'bg-gray-50 border-gray-300' 
          : 'bg-gray-800 border-gray-700'
      }`}>
        <div className={`text-center text-sm font-semibold ${
          tvTheme === 'light' ? 'text-gray-700' : 'text-gray-300'
        }`}>
          Total Count: {waitlist.length}
        </div>
        {table.number_of_tables > 1 && (
          <div className={`text-center text-xs mt-1 ${
            tvTheme === 'light' ? 'text-gray-500' : 'text-gray-400'
          }`}>
            # of Tables: {table.number_of_tables}
          </div>
        )}
      </div>
    </div>
  );
}
